import { useState } from 'react';
import { Link } from 'react-router-dom';
import { SwipaWordmark } from '@/icons';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { authApi } from '@/api/auth';
import { extractError } from '@/api/client';
import { CloudflareTurnstile } from '@/components/CloudflareTurnstile';

const schema = z.object({ email: z.string().email('Некорректный email') });
type FormData = z.infer<typeof schema>;

export default function ForgotPasswordPage() {
  const [sent, setSent] = useState(false);
  const [serverError, setServerError] = useState('');
  const [cfTurnstileToken, setCfTurnstileToken] = useState('');
  const [turnstileAttempt, setTurnstileAttempt] = useState(0);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  const onSubmit = async (data: FormData) => {
    setServerError('');
    try {
      await authApi.forgotPassword(data.email, cfTurnstileToken || undefined);
      setSent(true);
    } catch (err) {
      setServerError(extractError(err));
      setCfTurnstileToken('');
      setTurnstileAttempt((attempt) => attempt + 1);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-surface-950 flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <div className="text-5xl mb-4">📧</div>
          <h2 className="text-xl font-bold text-surface-50 mb-2">Ссылка отправлена</h2>
          <p className="text-surface-400 text-sm mb-6">
            Если аккаунт с таким email существует, вы получите ссылку для сброса пароля.
          </p>
          <Link to="/auth/login" className="btn-primary">
            Войти
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-950 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <SwipaWordmark size={44} base="#ffffff" accent="#C5243A" />
          <p className="text-surface-400 mt-3 text-sm">Восстановление пароля</p>
        </div>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="bg-surface-900 border border-surface-800 rounded-xl p-6 space-y-4"
        >
          {serverError && (
            <div className="bg-red-900/40 border border-red-700 text-red-300 text-sm rounded-lg px-3 py-2">
              {serverError}
            </div>
          )}
          <div>
            <label className="block text-surface-300 text-xs font-medium mb-1.5">Email</label>
            <input {...register('email')} className="irc-input" placeholder="ivan@example.com" />
            {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email.message}</p>}
          </div>
          <CloudflareTurnstile
            key={turnstileAttempt}
            onToken={setCfTurnstileToken}
            action="forgot-password"
          />
          <button
            type="submit"
            disabled={isSubmitting}
            className="btn-primary w-full"
          >
            {isSubmitting ? (
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              'Отправить ссылку'
            )}
          </button>
          <p className="text-center text-surface-400 text-sm">
            <Link to="/auth/login" className="text-brand-400 hover:text-brand-300">
              Назад к входу
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
